import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  EstimateInvalidError,
  EstimateRefusedError,
  useConvertEstimate,
  type Estimate,
} from '@/routes/estimates/useEstimates';

/**
 * Converting an approved estimate into a job (M2-09; Kyle, 2026-10-01): the
 * office's next act once the customer has said yes. Rendered by the estimate
 * page only for `customers.manage`, only for an Approved estimate that has not
 * been converted — the API refuses everything else, and says why if asked.
 *
 * Three shapes, because the act differs:
 * - an estimate drafted on an open job ADDS its lines to that job;
 * - one with no job CREATES an unscheduled job for dispatch to place;
 * - (M2-09 follow-up; Kyle, 2026-10-02) one drafted on a job that is FINISHED
 *   — the diagnostic visit is Complete or invoiced before the customer says
 *   yes — can't add to it. The API says why (`addToJobRefusal`, Convert's own
 *   sentence), so the dialog shows "Add to job" disabled with that reason and
 *   offers "Create a new job" (`mode: NewJob`). The estimate keeps the job it
 *   was drafted on, and the new job lists it as its source.
 *
 * An archived customer or property is converted anyway (Kyle, 2026-10-02),
 * and the dialog says so before the office confirms.
 *
 * Every shape goes through a confirm step (it writes to a job the dispatcher
 * and technician will act on, and there is no undo), and on success the office
 * lands on the job — including when the API answers that it had already been
 * converted, since that answer names the same job.
 */
export function ConvertToJob({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = useId();
  const convert = useConvertEstimate(estimate.id, estimate.propertyId);
  const [confirming, setConfirming] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const onJob = estimate.jobId !== null;
  // Only an estimate on a job can be refused "add"; the API sends null otherwise.
  const addRefusal = onJob ? estimate.addToJobRefusal : null;
  const newJob = !onJob || addRefusal !== null;
  const count = estimate.lines.length;
  const number = Number(estimate.number);
  const customer = estimate.customerName;

  const archived =
    estimate.customerArchived && estimate.propertyArchived
      ? t('estimates.convert.archived.both', { customer })
      : estimate.customerArchived
        ? t('estimates.convert.archived.customer', { customer })
        : estimate.propertyArchived
          ? t('estimates.convert.archived.property')
          : null;

  function confirm() {
    setProblem(null);
    convert.mutate(addRefusal !== null ? 'NewJob' : undefined, {
      onSuccess: (converted) => navigate(`/jobs/${converted.jobId}`),
      onError: (error) =>
        setProblem(
          error instanceof EstimateRefusedError || error instanceof EstimateInvalidError
            ? t('estimates.convert.refused', { reason: error.message })
            : t('estimates.convert.failed'),
        ),
    });
  }

  const help =
    addRefusal !== null
      ? t('estimates.convert.finishedHelp', { count, customer })
      : onJob
        ? t('estimates.convert.addHelp', { count })
        : t('estimates.convert.createHelp', { count, customer });
  const openLabel =
    addRefusal !== null
      ? t('estimates.convert.newJob')
      : onJob
        ? t('estimates.convert.add')
        : t('estimates.convert.create');
  const title =
    addRefusal !== null
      ? t('estimates.convert.newJobTitle', { number })
      : onJob
        ? t('estimates.convert.addTitle', { number })
        : t('estimates.convert.createTitle', { number });
  const description =
    addRefusal !== null
      ? t('estimates.convert.newJobDescription', { count })
      : onJob
        ? t('estimates.convert.addDescription', { count })
        : t('estimates.convert.createDescription', { count });
  const confirmLabel = convert.isPending
    ? t('estimates.convert.converting')
    : addRefusal !== null
      ? t('estimates.convert.newJobConfirm')
      : newJob
        ? t('estimates.convert.createConfirm')
        : t('estimates.convert.addConfirm');

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('estimates.convert.heading')}
      </h2>
      <p className="text-sm text-[var(--color-muted-foreground)]">{help}</p>
      <Button
        type="button"
        disabled={convert.isPending}
        onClick={() => {
          setProblem(null);
          setConfirming(true);
        }}
      >
        {openLabel}
      </Button>

      {confirming ? (
        <Dialog
          titleId={`${id}-confirm-title`}
          title={title}
          description={description}
          onClose={() => setConfirming(false)}
          testId="convert-estimate-dialog"
          footer={
            // Cancel first, so the dialog opens with focus on it — the safe
            // default for an act with no undo (ConfirmDialog's rule).
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirming(false)}
              >
                {t('common.cancel')}
              </Button>
              {addRefusal !== null ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled
                  aria-describedby={`${id}-add-refusal`}
                >
                  {t('estimates.convert.add')}
                </Button>
              ) : null}
              <Button type="button" size="sm" disabled={convert.isPending} onClick={confirm}>
                {confirmLabel}
              </Button>
            </div>
          }
        >
          {addRefusal !== null ? (
            <p id={`${id}-add-refusal`} className="text-sm" data-testid="convert-add-refusal">
              {t('estimates.convert.addUnavailable', { reason: addRefusal })}
            </p>
          ) : null}
          {archived ? (
            <p
              className="rounded-md border border-[var(--color-border)] p-2 text-sm"
              data-testid="convert-archived-warning"
            >
              {archived}
            </p>
          ) : null}
          {problem ? (
            <p role="alert" className="text-sm text-[var(--color-destructive)]">
              {problem}
            </p>
          ) : null}
        </Dialog>
      ) : null}
    </section>
  );
}
